import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MovixLogo from "@/components/MovixLogo";
import { COMPANY } from "@/lib/company";
import {
  LayoutDashboard, Car, Users, FileText, CreditCard,
  Wrench, MapPin, BarChart3, LogOut, Phone, Mail, MapPinned, MessageCircle, TrendingUp, Sunrise,
  Activity,
  AlertTriangle, Route as RouteIcon, UserCog,
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, key: "dashboard", end: true, testid: "nav-dashboard" },
  { to: "/hoje", icon: Sunrise, key: "today", testid: "nav-today" },
  { to: "/frota", icon: Car, key: "fleet", testid: "nav-frota" },
  { to: "/clientes", icon: Users, key: "customers", testid: "nav-clientes" },
  { to: "/alugueis", icon: FileText, key: "rentals", testid: "nav-alugueis" },
  { to: "/cobranca", icon: MessageCircle, key: "billing", testid: "nav-cobranca" },
  { to: "/pagamentos", icon: CreditCard, key: "payments", testid: "nav-pagamentos" },
  { to: "/manutencao", icon: Wrench, key: "maintenance", testid: "nav-manutencao" },
  { to: "/multas", icon: AlertTriangle, key: "tickets", testid: "nav-multas" },
  { to: "/pedagios", icon: RouteIcon, key: "tolls", testid: "nav-pedagios" },
  { to: "/gps", icon: MapPin, key: "gps", testid: "nav-gps" },
  { to: "/financeiro", icon: BarChart3, key: "financial", testid: "nav-financeiro", adminOnly: true },
  { to: "/bi", icon: TrendingUp, key: "bi", testid: "nav-bi", adminOnly: true },
  { to: "/atividade", icon: Activity, key: "activity", testid: "nav-atividade", adminOnly: true },
  { to: "/usuarios", icon: UserCog, key: "users", testid: "nav-usuarios", adminOnly: true },
];

function Sidebar() {
  const { user, logout } = useAuth();
  const { t } = useT();
  return (
    <aside className="w-64 shrink-0 bg-[#0A0A0A] text-white flex flex-col border-r border-zinc-800 min-h-screen sticky top-0">
      <div className="px-6 py-8 border-b border-zinc-800 flex items-center justify-center">
        <MovixLogo variant="light" showTagline size="md" />
      </div>

      <nav className="flex-1 px-3 py-6 space-y-0.5">
        <div className="overline text-zinc-500 px-3 mb-3">{t("common.operations")}</div>
        {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#F5A623] text-black"
                  : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            <span>{t(`sidebar.${n.key}`)}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-zinc-800 p-4 space-y-3">
        <div className="space-y-1.5 pb-3 border-b border-zinc-800">
          <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-[#F5A623] transition-colors">
            <Phone className="w-3 h-3" /> {COMPANY.phone}
          </a>
          <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-[#F5A623] transition-colors truncate">
            <Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{COMPANY.email}</span>
          </a>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <MapPinned className="w-3 h-3 shrink-0" /> {COMPANY.address}
          </div>
        </div>
        <div>
          <div className="overline text-zinc-500 mb-1">{t("common.sessionLabel")}</div>
          <Link
            to="/profile"
            className="block group"
            data-testid="sidebar-profile-link"
          >
            <div className="text-sm font-medium truncate group-hover:text-[#F5A623] transition-colors" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate group-hover:text-zinc-300">{user?.email}</div>
            <div className="text-[10px] font-mono uppercase mt-0.5 text-[#F5A623] opacity-0 group-hover:opacity-100 transition-opacity">{t("sidebar.profile")} →</div>
          </Link>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full inline-flex items-center justify-center gap-2 border border-zinc-800 hover:border-[#F5A623] hover:text-[#F5A623] text-zinc-300 px-3 py-2 text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" /> {t("common.logout")}
        </button>
      </div>
    </aside>
  );
}

export default function DashboardLayout() {
  const location = useLocation();
  const { t } = useT();
  const current = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
  const label = current ? t(`sidebar.${current.key}`) : t("sidebar.dashboard");
  return (
    <div className="min-h-screen flex bg-white">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between px-8 py-5">
            <div>
              <div className="overline">{label}</div>
              <h1 className="font-display font-bold text-2xl tracking-tighter">{label}</h1>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <div className="hidden md:flex items-center gap-2 text-xs text-zinc-500 font-mono">
                <span className="w-2 h-2 bg-green-500 inline-block" /> {t("common.systemOnline")}
              </div>
            </div>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
